import { CurrencyPipe } from '@angular/common';
import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

@Directive({
  selector: '[pbCurrencyFormatter]',
  standalone: true
})
export class CurrencyFormatterDirective implements OnInit, OnDestroy {
  @Input('disableDecimals') public disableDecimals: string = '';

  @Input('currencyCode') public set currencyCode(value: string) {
    let fieldValue = this.el.nativeElement.value;
    if (fieldValue) {
      fieldValue = this.removeFormat(fieldValue);
    }

    this._currencyCode = value;
    if (fieldValue) {
      this.format(fieldValue);
    }
  }

  @Input('emptyState') public set emptyState(value: boolean) {
    this._emptyState = value;
  }

  private decimalSeparator: string;
  private groupSeparator: string;
  private isFocused = false;
  private subscriptions: Subscription[] = [];
  private _currencyCode: string = '';
  private _emptyState: boolean = false;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private currencyPipe: CurrencyPipe,
    private control: NgControl
  ) {
    this.decimalSeparator = ',';
    this.groupSeparator = ' ';
  }

  get currencySymbol(): string {
    return Intl.NumberFormat('en', { style: 'currency', currency: this._currencyCode })
      .formatToParts()
      .find((part) => part.type === 'currency')!.value;
  }

  private formatAmount(value: string | number) {
    if (typeof value == 'string') {
      value = value.replace(',', '.');
    }
    let newValue = this.currencyPipe.transform(value, this._currencyCode, 'symbol', '1.2-2');
    return newValue?.replace('.', '');
  }

  ngOnInit(): void {
    const amount = this.formatAmount(this.el.nativeElement.value);

    if (!this._emptyState) {
      this.control.valueAccessor?.writeValue(amount || '0');
    } else {
      this.control.valueAccessor?.writeValue(amount || '');
    }

    if (this.control.control) {
      this.subscriptions.push(
        this.control.control.valueChanges.subscribe((value) => {
          if (!this.isFocused) {
            const amount = this.formatAmount(value);
            const emptyFormat = this.formatAmount('0');
            this.el.nativeElement.value = amount || emptyFormat || '0';
          }
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private format(v: string) {
    const value = parseFloat(v.replace(',', '.'));

    if (isNaN(value)) {
      if (!this._emptyState) {
        this.control.control?.patchValue(0);
        const emptyFormat = this.formatAmount('0');
        this.el.nativeElement.value = emptyFormat || '0';
      } else {
        this.el.nativeElement.value = '';
      }
      return;
    }

    const amount = this.formatAmount(value);

    this.control.control?.patchValue(Math.round(value * 100) / 100);
    this.control.valueAccessor?.writeValue(amount || '0');
  }

  private removeFormat(value: string) {
    const regex = new RegExp(`[\\${this.groupSeparator}${this.currencySymbol}]`, 'g');

    let fieldValue = value.replace(regex, '').trim().replace(this.decimalSeparator, '.');
    const amount = parseFloat(fieldValue);

    if (amount % 1 !== 0) {
      fieldValue = amount.toFixed(2).replace('.', this.decimalSeparator);
    } else {
      fieldValue = amount.toFixed(0);
    }

    return fieldValue;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    const val = this.disableDecimals ? value.replace(/[^0-9]/g, '') : value.replace(/[^0-9\,\.]/g, '');
    const amount = this.disableDecimals ? parseInt(val) : parseFloat(val);

    if (!isNaN(amount)) {
      this.control.control?.patchValue(amount, { emitEvent: false });
      this.el.nativeElement.value = val;
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    const pastedText = event.clipboardData?.getData('text');
    if (pastedText) {
      let value = this.removeFormat(pastedText);
      this.control.valueAccessor?.writeValue(value);
      this.el.nativeElement.select();
    }
    event.preventDefault();
  }

  // Fix for TMWEB-1752 Safari deselect on mouseup
  @HostListener('mouseup', ['$event'])
  onMouseUp(event: MouseEvent) {
    event.preventDefault();
  }

  @HostListener('focus')
  onFocus() {
    this.isFocused = true;
    let fieldValue = this.el.nativeElement.value;
    if (!fieldValue) {
      return;
    }

    fieldValue = this.removeFormat(fieldValue);

    this.control.valueAccessor?.writeValue(fieldValue);
    this.el.nativeElement.select();
  }

  @HostListener('blur', ['$event'])
  onBlur(event: FocusEvent) {
    this.isFocused = false;
    const target = event.target as HTMLInputElement | null;
    this.format(target?.value ?? '');
  }
}
